import React, { useState } from "react"
import { View, ScrollView, StyleSheet, Pressable } from "react-native"
import { theme } from "@common/theme/theme"
import TextM from "@common/components/text/TextM"
import TitleM from "@common/components/text/TitleM"
import { BadgeM } from "@common/components/badge/BadgeM"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import FeedbackButton from "@common/components/feedback/FeedbackButton"
import QuizQuestionSignalButton from "../quiz/components/QuizQuestionSignalButton"

/**
 * PrerequisiteQuizDisplay - Shared collapsible quiz correction, used by both
 * the tutor review and the student review.
 *
 * Compact collapsible view:
 * - Collapsed (default): question number, question text, student answer, result indicator
 * - Expanded: full question text and all options with visual feedback
 *
 * Data source (one of):
 * - `activity` — tutor path: reads `activity.prerequisite_quiz` + `activity.prerequisite_quiz_attempt`
 * - `quiz` — student path: a `QuizResource` shape (`{ questions, latest_attempt }`)
 *
 * Both shapes share the same question (`id/question_text/options/correct_answer`)
 * and answer (`quiz_question_id/answer/is_correct`) fields, so a single renderer
 * serves both.
 *
 * @param {Object} props
 * @param {Object} [props.activity] - Tutor data source
 * @param {Object} [props.quiz] - Student data source (QuizResource)
 * @param {number|null} [props.quizFeedback] - Current feedback value (1, -1, or null)
 * @param {Function} [props.onQuizFeedback] - Callback when feedback is given
 * @param {boolean} [props.showScore=true] - Show the score header
 * @param {boolean} [props.showFeedback=true] - Show the tutor feedback buttons
 * @param {boolean} [props.showSignalButton=true] - Show the "Signaler" button (tutor)
 * @param {string|null} [props.title] - Optional heading above the list
 * @param {boolean} [props.scrollable=true] - Wrap in a ScrollView (tutor full screen) vs a plain View (embedded)
 */
const PrerequisiteQuizDisplay = (props) => {
    const {
        activity,
        quiz,
        quizFeedback,
        onQuizFeedback,
        showScore = true,
        showFeedback = true,
        showSignalButton = true,
        title = null,
        scrollable = true,
        wrapPreview = false,
    } = props
    const [expandedQuestions, setExpandedQuestions] = useState({})

    // Normalize the data source (student `quiz` prop or tutor `activity` prop)
    const quizData = quiz || activity?.prerequisite_quiz
    const latestAttempt = quiz?.latest_attempt ?? activity?.prerequisite_quiz_attempt
    const signalActivityId = activity?.id
    const isEmbedded = !!quiz || !scrollable

    if (!quizData || !quizData.questions || quizData.questions.length === 0) {
        if (isEmbedded) return null
        return (
            <View style={styles.container}>
                <TextM color={theme.colors.grey}>Aucun quiz prérequis disponible</TextM>
            </View>
        )
    }

    // Determine if student has completed the quiz
    const hasCompleted = !!latestAttempt
    const studentAnswers = latestAttempt?.answers || []
    const score = latestAttempt?.score || 0
    const total = quizData.questions.length
    const passed = latestAttempt?.passed || false

    // Helper to find student's answer for a question
    const getStudentAnswer = (questionId) => {
        return studentAnswers.find(ans => ans.quiz_question_id === questionId)
    }

    // Helper to check if an option is the correct answer
    const isCorrectAnswer = (question, option) => {
        return question.correct_answer === option
    }

    // Helper to check if student selected this option
    const isStudentAnswer = (questionId, option) => {
        const studentAns = getStudentAnswer(questionId)
        return studentAns?.answer === option
    }

    // Toggle question expansion
    const toggleQuestion = (questionId) => {
        setExpandedQuestions(prev => ({
            ...prev,
            [questionId]: !prev[questionId]
        }))
    }

    // Render collapsed question row
    const renderCollapsedQuestion = (question, index) => {
        const studentAns = getStudentAnswer(question.id)
        const isCorrect = studentAns?.is_correct
        const studentAnswerText = studentAns?.answer

        // Determine colors based on result
        const bgColor = hasCompleted
            ? (isCorrect ? theme.colors.successLight : theme.colors.errorLight)
            : theme.colors.white
        const borderColor = hasCompleted
            ? (isCorrect ? theme.colors.success : theme.colors.error)
            : theme.colors.light
        const iconName = isCorrect ? "check" : "times"
        const iconColor = isCorrect ? theme.colors.success : theme.colors.error

        return (
            <Pressable
                key={question.id}
                onPress={() => toggleQuestion(question.id)}
                style={[
                    styles.collapsedRow,
                    {
                        backgroundColor: bgColor,
                        borderColor: borderColor,
                    }
                ]}
            >
                <View style={styles.collapsedLeft}>
                    <View style={[
                        styles.questionBadge,
                        { backgroundColor: hasCompleted ? (isCorrect ? theme.colors.success : theme.colors.error) : theme.colors.primary }
                    ]}>
                        <TextM fontWeight="Bold" fontSize={12} color={theme.colors.white}>
                            Q{index + 1}
                        </TextM>
                    </View>
                    <View style={styles.collapsedTextContainer}>
                        <TextM
                            fontWeight="Medium"
                            fontSize={13}
                            wrap={wrapPreview}
                        >
                            {question.question_text}
                        </TextM>
                        {hasCompleted && (
                            <TextM
                                fontWeight="Regular"
                                fontSize={12}
                                color={theme.colors.grey}
                                wrap={wrapPreview}
                                style={{ marginTop: 2 }}
                            >
                                → {studentAnswerText || "Pas de réponse"}
                            </TextM>
                        )}
                    </View>
                </View>
                <View style={styles.collapsedRight}>
                    {hasCompleted && (
                        <FontAwesome5
                            name={iconName}
                            size={14}
                            color={iconColor}
                            style={{ marginRight: 8 }}
                        />
                    )}
                    <FontAwesome5
                        name="chevron-down"
                        size={12}
                        color={theme.colors.grey}
                    />
                </View>
            </Pressable>
        )
    }

    // Render expanded question
    const renderExpandedQuestion = (question, index) => {
        const studentAns = getStudentAnswer(question.id)
        const isQuestionCorrect = studentAns?.is_correct

        return (
            <View
                key={question.id}
                style={[
                    styles.expandedContainer,
                    {
                        borderColor: hasCompleted
                            ? (isQuestionCorrect ? theme.colors.success : theme.colors.error)
                            : theme.colors.light,
                    }
                ]}
            >
                {/* Header with collapse button */}
                <Pressable
                    onPress={() => toggleQuestion(question.id)}
                    style={styles.expandedHeader}
                >
                    <View style={[
                        styles.questionBadge,
                        { backgroundColor: hasCompleted ? (isQuestionCorrect ? theme.colors.success : theme.colors.error) : theme.colors.primary }
                    ]}>
                        <TextM fontWeight="Bold" fontSize={12} color={theme.colors.white}>
                            Q{index + 1}
                        </TextM>
                    </View>
                    <TextM fontWeight="SemiBold" fontSize={14} wrap style={{ flex: 1, marginHorizontal: 10 }}>
                        {question.question_text}
                    </TextM>
                    {showSignalButton && signalActivityId && (
                        <QuizQuestionSignalButton
                            activityId={signalActivityId}
                            questionId={question.id}
                            context="review"
                            label="Signaler"
                        />
                    )}
                    <FontAwesome5
                        name="chevron-up"
                        size={12}
                        color={theme.colors.grey}
                    />
                </Pressable>

                {/* Options list */}
                <View style={styles.optionsContainer}>
                    {question.options?.map((option, optionIndex) => {
                        const isCorrect = isCorrectAnswer(question, option)
                        const isStudentChoice = hasCompleted && isStudentAnswer(question.id, option)
                        const isStudentCorrect = isStudentChoice && studentAns?.is_correct
                        const isStudentIncorrect = isStudentChoice && !studentAns?.is_correct

                        // Determine styling
                        let backgroundColor = theme.colors.white
                        let borderColor = theme.colors.light
                        let showIcon = false
                        let iconName = ""
                        let iconColor = ""

                        if (hasCompleted) {
                            if (isStudentCorrect) {
                                // Student selected correct answer
                                backgroundColor = theme.colors.successLight
                                borderColor = theme.colors.success
                                showIcon = true
                                iconName = "check-circle"
                                iconColor = theme.colors.success
                            } else if (isStudentIncorrect) {
                                // Student selected wrong answer
                                backgroundColor = theme.colors.errorLight
                                borderColor = theme.colors.error
                                showIcon = true
                                iconName = "times-circle"
                                iconColor = theme.colors.error
                            } else if (isCorrect) {
                                // This is the correct answer but student didn't select it
                                backgroundColor = theme.colors.primaryLighter
                                borderColor = theme.colors.primary
                                showIcon = true
                                iconName = "check-circle"
                                iconColor = theme.colors.primary
                            }
                        } else if (isCorrect) {
                            // Quiz not attempted yet, show correct answer for tutor
                            backgroundColor = theme.colors.primaryLighter
                            borderColor = theme.colors.primary
                            showIcon = true
                            iconName = "check-circle"
                            iconColor = theme.colors.primary
                        }

                        return (
                            <View
                                key={`${question.id}-option-${optionIndex}`}
                                style={[
                                    styles.optionItem,
                                    { backgroundColor, borderColor }
                                ]}
                            >
                                <TextM
                                    fontWeight={isStudentChoice ? "SemiBold" : "Regular"}
                                    fontSize={13}
                                    wrap
                                    style={{ flex: 1 }}
                                >
                                    {option}
                                </TextM>
                                {showIcon && (
                                    <FontAwesome5
                                        name={iconName}
                                        size={16}
                                        color={iconColor}
                                        solid
                                        style={{ marginLeft: 10 }}
                                    />
                                )}
                            </View>
                        )
                    })}
                </View>
            </View>
        )
    }

    // Render a single question (collapsed or expanded)
    const renderQuestion = (question, index) => {
        const isExpanded = expandedQuestions[question.id]
        return isExpanded
            ? renderExpandedQuestion(question, index)
            : renderCollapsedQuestion(question, index)
    }

    const content = (
        <>
            {title && (
                <TextM
                    fontWeight="Bold"
                    color={theme.colors.primary}
                    style={{ marginBottom: 8, alignSelf: "flex-start" }}
                >
                    {title}
                </TextM>
            )}

            {/* Score display for completed quizzes - compact version */}
            {showScore && hasCompleted && (
                <View style={styles.scoreContainer}>
                    <View style={styles.scoreRow}>
                        <TitleM fontSize={22} color={passed ? theme.colors.success : theme.colors.error}>
                            {score}/{total}
                        </TitleM>
                        <BadgeM
                            count={passed ? "Réussi" : "Échoué"}
                            size={16}
                            style={{
                                backgroundColor: passed ? theme.colors.success : theme.colors.error,
                                color: theme.colors.white,
                                marginLeft: 10,
                            }}
                        />
                    </View>
                </View>
            )}

            {/* Questions list */}
            <View style={styles.questionsListContainer}>
                {quizData.questions.map((question, index) => renderQuestion(question, index))}
            </View>

            {/* Feedback buttons - shown at bottom when student has completed the quiz */}
            {showFeedback && hasCompleted && onQuizFeedback && (
                <View style={styles.feedbackContainer}>
                    <TextM fontSize={11} color={theme.colors.grey} style={{ marginRight: 8 }}>
                        Ce quiz était-il utile ?
                    </TextM>
                    {quizFeedback !== null ? (
                        <FeedbackButton
                            onPress={() => {}}
                            disabled={true}
                            isSelected={true}
                            isPositive={quizFeedback === 1}
                            tooltipText={quizFeedback === 1 ? "Utile" : "Pas utile"}
                            minimal={true}
                        />
                    ) : (
                        <View style={styles.feedbackButtons}>
                            <FeedbackButton
                                onPress={() => onQuizFeedback(1)}
                                disabled={quizFeedback !== null}
                                isSelected={quizFeedback === 1}
                                isPositive={true}
                                tooltipText="Utile"
                                minimal={true}
                            />
                            <FeedbackButton
                                onPress={() => onQuizFeedback(-1)}
                                disabled={quizFeedback !== null}
                                isSelected={quizFeedback === -1}
                                isPositive={false}
                                tooltipText="Pas utile"
                                minimal={true}
                            />
                        </View>
                    )}
                </View>
            )}
        </>
    )

    if (scrollable) {
        return (
            <ScrollView
                style={styles.container}
                contentContainerStyle={styles.scrollContent}
            >
                {content}
            </ScrollView>
        )
    }

    return <View style={styles.embeddedContainer}>{content}</View>
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.whiteAlmost,
    },
    embeddedContainer: {
        width: "100%",
    },
    scrollContent: {
        paddingBottom: 40,
        paddingTop: 10,
    },
    scoreContainer: {
        paddingHorizontal: 15,
        paddingVertical: 8,
        marginBottom: 5,
    },
    scoreRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
    },
    feedbackContainer: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        marginTop: 20,
        paddingHorizontal: 15,
    },
    feedbackButtons: {
        flexDirection: "row",
        gap: 8,
    },
    questionsListContainer: {
        paddingHorizontal: 10,
        gap: 8,
    },
    // Collapsed row styles
    collapsedRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingVertical: 10,
        paddingHorizontal: 12,
        borderRadius: 10,
        borderWidth: 1.5,
    },
    collapsedLeft: {
        flexDirection: "row",
        alignItems: "center",
        flex: 1,
    },
    collapsedRight: {
        flexDirection: "row",
        alignItems: "center",
    },
    collapsedTextContainer: {
        marginLeft: 10,
        flex: 1,
    },
    questionBadge: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 6,
        minWidth: 32,
        alignItems: "center",
    },
    // Expanded styles
    expandedContainer: {
        backgroundColor: theme.colors.white,
        borderRadius: 10,
        borderWidth: 1.5,
        overflow: "hidden",
    },
    expandedHeader: {
        flexDirection: "row",
        alignItems: "center",
        padding: 12,
        backgroundColor: theme.colors.whiteAlmost,
    },
    optionsContainer: {
        padding: 10,
        gap: 6,
    },
    optionItem: {
        flexDirection: "row",
        alignItems: "center",
        padding: 10,
        borderRadius: 8,
        borderWidth: 1,
    },
})

export default PrerequisiteQuizDisplay
